const Address = require('@/lib/Addresses/Address');
const CurrentRedemptionAction = require('./CurrentRedemptionAction');
const Member = require('@/lib/Memberships/Member');
const MembershipDataset = require('@/lib/Memberships/MembershipDataset');
const MembershipPlan = require('@/lib/Memberships/MembershipPlan');
const MembershipRefundAmount = require('@/lib/Memberships/MembershipRefundAmount');
const MembershipRenewalDateTypes = require('@/lib/Memberships/MembershipRenewalDateTypes');
const MembershipSubscription = require('@/lib/Memberships/MembershipSubscription');
const { cloneDeep } = require('@/functions/clone');
const { populate } = require('@/functions/objects');
const { toDate, toInt, toNullableBool, toString } = require('@/functions/transform');

/**
 * @param {any} data
 *
 * @constructor
 */
const Membership = function (data = {}) {
    /**
     * @type {number}
     */
    this.ID = null;

    /**
     * @type {string}
     */
    this.ref = '';

    /**
     * @type {Address}
     */
    this.address = null;

    /**
     * @type {CustomerAccount|null}
     */
    this.customerAccount = null;

    /**
     * @type {MembershipSubscription[]}
     */
    this.subscriptions = [];

    /**
     * Do any of the subscriptions covering today or starting in the future have installmentAmounts?
     *
     * @type {boolean}
     */
    this.hasInstallments = false;

    /**
     * @type {Date}
     */
    this.initialStartDate = null;

    this.status = 'Active';

    /**
     * @type {boolean}
     */
    this.currentlyProcessing = false;

    /**
     * @type {string}
     */
    this.currentRedemptionAction = CurrentRedemptionAction.NONE;

    /**
     * @type {MembershipPlan|null}
     */
    this.currentPlan = null;

    /**
     * @type {MembershipSubscription|null}
     */
    this.currentSubscription = null;

    /**
     * @type {null|bool}
     */
    this.autoRenew = null;

    /**
     * @type {MembershipPlan|null}
     */
    this.renewalPlan = null;

    /**
     * @type {Date}
     */
    this.renewToDate = null;

    /**
     * @type {Date}
     */
    this.maxSubscriptionExpiryDate = null;

    /**
     * @type {MembershipRefundAmount|null}
     */
    this.refundAmount = null;

    /**
     * @type {Member[]}
     */
    this.members = [];

    /**
     * Used when listing this membership on the Redeem search results.
     * If the search was for a contact's ref and that brings up multiple results, all the memberships
     * that contact is on will be listed. Clicking on one of them should take the user to the membership
     * with that contact highlighted.
     * This field is used to say which contact ref should be highlighted when it is clicked on from the
     * search results.
     *
     * @type {string|null}
     */
    this.highlightMemberRef = null;

    /**
     * @type {Date}
     */
    this.earliestAllowedRenewalDate = null;

    /**
     * @type {{type: string|null, chosenDate: Date|null}}
     */
    this.chosenRenewalOption = {
        type: null,
        chosenDate: null,
    };

    populate(this, data);
};

Membership.prototype = {
    getHydrationMap() {
        return {
            address: {
                model: Address,
            },
            autoRenew: toNullableBool,
            chosenRenewalOption: {},
            currentPlan: {
                field(data) {
                    if (data.currentPlan) {
                        return data.currentPlan;
                    }

                    if (
                        data.hasOwnProperty('currentSubscription')
                            && data.currentSubscription
                            && data.currentSubscription.hasOwnProperty('membershipplans')
                    ) {
                        return data.currentSubscription.membershipplans;
                    }

                    // Returning undefined so the value does not get overwritten when re-hydrating
                    return undefined;
                },
                model: MembershipPlan,
            },
            currentSubscription: {
                model: MembershipSubscription,
            },
            customerAccount: {
                transform(value, allValues, hydrator) {
                    if (!value) {
                        return null;
                    }
                    // Import CustomerAccount here to avoid a circular dependency if it were imported up the top.
                    // This could be avoided with ES6 imports.
                    const CustomerAccount = require('@/lib/Customers/CustomerAccount');
                    return hydrator.hydrate(value, new CustomerAccount());
                },
            },
            earliestAllowedRenewalDate: toDate,
            ID: {
                field: ['ID', 'membershipID'],
                transform: toInt,
            },
            initialStartDate: toDate,
            members: {
                field(data) {
                    if (data.isHydrated) {
                        return data.members;
                    }

                    // Reduce the members->contact to just contact (but with the memberID in the contact).
                    if (data.hasOwnProperty('members')) {
                        return data.members.map(function (member) {
                            // It turns out you *can* have a member without a contact, so work around that
                            member.contacts = member.contacts || {};

                            // Keep just the contact data separate so it can be used to create a regular contact model
                            // instead of mixing up the contact and member data.
                            let contactData = {};
                            contactData = Object.assign(contactData, member.contacts);
                            member.contacts.contacts = contactData;

                            member.contacts.memberID = member.memberID;
                            // And when the member object has more fields, you have to know to manually add them here...
                            member.contacts.visitedToday = member.isRedeemedForDay || false;
                            member.contacts.visitCount = member.visitCount || 0;

                            return member.contacts;
                        });
                    }

                    return [];
                },
                modelCollection: Member,
            },
            ref: toString,
            refundAmount: {
                model: MembershipRefundAmount,
            },
            renewalPlan: {
                model: MembershipPlan,
            },
            renewToDate: toDate,
            status: toString,
            subscriptions: {
                modelCollection: MembershipSubscription,
            },
        };
    },

    afterHydration() {
        // We want the expiry date of the latest subscription.
        this.populateMaxSubscriptionExpiryDate();

        // Make sure the address is an address object, even if it's empty.
        if (!this.address || !(this.address instanceof Address)) {
            this.address = new Address();
        }

        this.hasInstallments = this.getFutureSubscriptionsWithInstallments().length > 0;

        for (let i = 0; i < this.members.length; i++) {
            this.members[i].setIndex(i);
        }
    },

    getRef: function getRef() {
        return this.ref;
    },

    setRef: function setRef(newRef) {
        this.ref = newRef;
    },

    getID() {
        return this.ID;
    },

    isActive: function isActive() {
        return this.status === 'Active';
    },

    getStatus: function getStatus() {
        return this.status.toUpperCase();
    },

    isDead: function isDead() {
        return this.status === 'Deleted';
    },

    setHighlightStatus: function highlightStatus(highlight) {
        this.highlightStatus = highlight;
    },

    /**
     * @return {Member[]}
     */
    getMembers() {
        return this.members;
    },

    /**
     * When a membership supports multiple members, and those additional members are optional, the app will still
     * create 'members' for every possible slot on the membership. This additional members that were never actually
     * used will have a memberID, but no contactID.
     * This method returns on real members - those that are actually filled.
     *
     * @return {Member[]}
     */
    getFilledMembers() {
        return this.members.filter((m) => m.hasContact());
    },

    /**
     * Checking if Membership is postponed
     *
     * @return {boolean} returns true if postponed otherwise false
     */
    isPostponed: function isPostponed() {
        let now = new Date();
        now.setHours(23, 59, 59, 59);
        return (this.getInitialStartDate() > now);
    },

    /**
     * Getting the initial start date of membership
     *
     * @return {Date} Initial start date or null if there is no subscription
     */
    getInitialStartDate: function getInitialStartDate() {
        return this.initialStartDate;
    },

    /**
     * Returns the first active MembershipSubscription.
     *
     * @returns {MembershipSubscription|null}
     */
    getCurrentSubscription: function getCurrentSubscription() {
        let latestSubscription = null;
        for (let i = 0; i < this.subscriptions.length; i++) {
            let subscription = this.subscriptions[i];

            if (subscription.isActive()) {
                return subscription;
            }

            if (!latestSubscription || subscription.startDate > latestSubscription.startDate) {
                latestSubscription = subscription;
            }
        }

        return latestSubscription;
    },

    /**
     * @param {number} memberID
     *
     * @return {Member|null}
     */
    getMemberByID(memberID) {
        return this.members.find((m) => m.ID === memberID) || null;
    },

    /**
     * @param {number} contactID
     *
     * @return {Member|null}
     */
    getMemberByContactID(contactID) {
        return this.members.find((m) => m.contactID === contactID) || null;
    },

    /**
     * @param {string} contactRef
     *
     * @return {Member|null}
     */
    getMemberByRef(contactRef) {
        contactRef = contactRef.toUpperCase();
        return this.members.find((m) => m.contact && m.contact.ref && m.contact.ref.toUpperCase() === contactRef) || null;
    },

    setMemberVisits: function setMemberVisits(memberRedemptionData) {
        for (let redemptionIndex = 0; redemptionIndex < memberRedemptionData.length; redemptionIndex++) {
            let redemptionData = memberRedemptionData[redemptionIndex];
            let member = this.getMemberByID(redemptionData.memberID);
            member.setVisits({
                isRedeemedForDay: redemptionData.isRedeemedForDay,
                visitCount: redemptionData.visitCount,
            });
        }
    },

    isRenewable: function isRenewable() {
        return !!this.renewalPlan;
    },

    isRenewing: function isRenewing() {
        // If user has chosen an option and it's not "don't renew", then it's renewing.
        return this.chosenRenewalOption.type && this.chosenRenewalOption.type !== MembershipRenewalDateTypes.NONE;
    },

    resetRenew: function resetRenew() {
        this.chosenRenewalOption.type = null;
        this.chosenRenewalOption.chosenDate = null;
    },

    setRenewalOption: function setRenewalOption(renewalType, renewalDate) {
        this.chosenRenewalOption.type = renewalType;
        this.chosenRenewalOption.chosenDate = renewalDate;
    },

    populateMaxSubscriptionExpiryDate: function populateMaxSubscriptionExpiryDate() {
        let maxDate = null;
        for (let i = 0; i < this.subscriptions.length; i++) {
            let subscription = this.subscriptions[i];
            if (maxDate == null || subscription.endDate > maxDate) {
                maxDate = subscription.endDate;
            }
        }
        this.maxSubscriptionExpiryDate = maxDate;
    },

    willAutoRenew: function willAutoRenew() {
        if (!this.isActive()) {
            return null;
        }

        if (this.autoRenew === false || this.autoRenew === true) {
            // Membership specifically says it will or won't.
            return this.autoRenew;
        }

        // It's up to the plan.
        if (this.currentPlan) {
            return this.currentPlan.autoRenew;
        }

        return null;
    },

    getAllPayments: function getAllPayments() {
        let subscription = this.getCurrentSubscription();

        if (!subscription) {
            return [];
        }

        return subscription.subscriptionPayments.sort(function (a, b) {
            return new Date(a.payment.takenAt) - new Date(b.payment.takenAt);
        });
    },

    getPaymentSummary: function getPaymentSummary() {
        let paymentsMade = 0.00;
        this.getAllPayments().forEach(function (subscriptionPayment) {
            if (subscriptionPayment.payment.paid) {
                paymentsMade += subscriptionPayment.apportionedAmount;
            }
        });

        let total = this.getCurrentSubscription().membershipPlan.getTotalPrice();

        return {
            total,
            paymentsMade,
            balance: (total - paymentsMade),
        };
    },

    /**
     * @return {MembershipSubscription[]}
     */
    getFutureSubscriptionsWithInstallments: function getFutureSubscriptionsWithInstallments() {
        let now = new Date();
        return this.subscriptions.filter(
            /**
             * @param {MembershipSubscription} subscription
             */
            function (subscription) {
                return subscription.hasInstallments() && subscription.endDate >= now;
            }
        );
    },

    /**
     * Return the data for this membership (including members) in a membership dataset for editing
     * in the MembershipDatasetsModal
     *
     * @return {MembershipDataset}
     */
    toMembershipDataset: function toMembershipDataset() {
        let dataset = new MembershipDataset(this.currentPlan);

        dataset.assignedMembershipRef = this.ref;
        dataset.address = cloneDeep(this.address);
        dataset.addressID = this.address.ID;
        dataset.membershipID = this.ID;
        dataset.membershipDateFrom = new Date(this.initialStartDate);

        dataset.members = [];
        for (let m = 0; m < this.members.length; m++) {
            let contact = this.members[m].contact ? cloneDeep(this.members[m].contact) : null;
            let member = cloneDeep(this.members[m]);
            member.contact = contact;
            dataset.members.push(member);
        }

        return dataset;
    },

    getRenewButtonText: function getRenewButtonText() {
        switch (this.chosenRenewalOption.type) {
            case MembershipRenewalDateTypes.EXPIRY:
                return 'MEMBERSHIPS.RENEWING_TO';

            case MembershipRenewalDateTypes.TODAY:
            case MembershipRenewalDateTypes.OTHER:
                return 'MEMBERSHIPS.RENEWING_FROM';

            case MembershipRenewalDateTypes.NONE:
            default:
                return 'MEMBERSHIPS.RENEW';
        }
    },

    getChosenRenewalFromDate: function getChosenRenewalFromDate() {
        let chosenDate = this.chosenRenewalOption.chosenDate;
        return chosenDate === null ? new Date() : chosenDate;
    },

    canBeRedeemedRightNow() {
        if (this.currentRedemptionAction !== CurrentRedemptionAction.NONE || !this.isActive()) {
            return false;
        }

        return this.currentPlan ? this.currentPlan.membershipsCanBeRedeemedToday : true;
    },

    /**
     * @return {Member[]}
     */
    getMembersRedeemedToday() {
        return this.members.filter((m) => m.hasVisitedToday());
    },

    /**
     * Has at least one of the members on this membership been redeemed/admitted today.
     *
     * @return {boolean}
     */
    hasRedeemedMembersToday() {
        return this.getMembersRedeemedToday().length > 0;
    },

    /**
     * Is there at least one member on this membership that has been been redeemed/admitted today.
     *
     * @return {boolean}
     */
    hasNonRedeemedMembersToday() {
        return this.getMembersRedeemedToday().length < this.getFilledMembers().length;
    },

    get redemptionActionInProgress() {
        return this.currentRedemptionAction !== CurrentRedemptionAction.NONE;
    },

    makeLean() {
        // Removes all the attached sub-objects from the membership object,
        // These are things are can cause circular references when storing in IndexedDB.
        this.address = null;
        this.customerAccount = null;
        this.subscriptions = []; // Note: currentSubscription is kept.
        this.currentSubscription.subscriptionPayments = [];
        this.members = [];
        this.renewalPlan = null;
    },
};

module.exports = Membership;
